// ============================================================================
// 📋 CLIENTMESSAGEHISTORY — Historial de Mensajes del Cliente (V1 Enterprise)
// src/features/mensagens/components/ClientMessageHistory.jsx
// ============================================================================
// 🔥 V1: Ultra Robusto · Ultra Rápido · WhatsApp-Grade · Enterprise-Ready
// ============================================================================
//
// ARQUITECTURA:
// - Usa useMensagemDetalhe (feature module) — ÚNICA fuente de verdad
// - Scroll infinito hacia arriba (30 msg por carga) como WhatsApp
// - Agrupación por fecha inteligente (Hoje, Ontem, data completa)
// - Attachments visibles (imagen/documento) con links a Cloudinary
// - Estados de mensaje completos (sending/sent/delivered/read/failed)
// - Memoización agresiva para 60fps incluso con 1000+ mensajes
// - Diseño responsive (compact/normal) para mobile/desktop
// - Reemplaza al MessageTimeline legacy (1 sola línea en ClientDetails)
// ============================================================================

import React, { useState, useCallback, useRef, useEffect, useMemo, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useMensagemDetalhe } from '../hooks/useMensagemDetalhe';
import { useIsMobile } from '../../../hooks/useMediaQuery';
import {
  MessageSquare,
  Clock,
  Check,
  CheckCheck,
  AlertCircle,
  Image,
  FileText,
  ExternalLink,
  Loader2,
  ChevronDown,
} from 'lucide-react';

// ============================================================================
// ⚙️ CONFIGURACIÓN
// ============================================================================
const SCROLL_THRESHOLD = 80;      // px desde el top para disparar carga
const MAX_HEIGHT_MOBILE = '340px'; // Altura máxima en mobile
const MAX_HEIGHT_DESKTOP = '420px'; // Altura máxima en desktop

// ============================================================================
// 🧩 SUB-COMPONENTES MEMOIZADOS (Render mínimo, 60fps)
// ============================================================================

// Separador de fecha memoizado
const DateLabel = memo(({ label }) => (
  <div className="flex items-center justify-center mb-2 sticky top-0 z-[1] py-1">
    <span className="text-[10px] text-slate-400 bg-white/90 backdrop-blur-sm rounded-full px-3 py-0.5 shadow-sm border border-slate-100">
      {label}
    </span>
  </div>
));
DateLabel.displayName = 'DateLabel';

// Ícono de estado memoizado
const StatusIcon = memo(({ status }) => {
  const iconProps = { size: 12, className: 'flex-shrink-0' };
  switch (status) {
    case 'sent':     return <Check {...iconProps} className="text-blue-400" />;
    case 'delivered': return <CheckCheck {...iconProps} className="text-gray-400" />;
    case 'read':     return <CheckCheck {...iconProps} className="text-blue-500" />;
    case 'failed':   return <AlertCircle {...iconProps} className="text-red-500" />;
    case 'sending':  return <Clock {...iconProps} className="text-gray-400 animate-pulse" />;
    default:         return <Check {...iconProps} className="text-gray-300" />;
  }
});
StatusIcon.displayName = 'StatusIcon';

// Attachment memoizado
const AttachmentLink = memo(({ att, outbound }) => (
  <a
    href={att.url}
    target="_blank"
    rel="noopener noreferrer"
    className={`flex items-center gap-1.5 p-1.5 rounded-lg text-[10px] transition-colors ${
      outbound
        ? 'bg-green-300/30 text-green-800 hover:bg-green-300/50'
        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
    }`}
    onClick={(e) => e.stopPropagation()}
  >
    {att.type === 'image' ? (
      <Image size={12} className="flex-shrink-0" />
    ) : (
      <FileText size={12} className="flex-shrink-0" />
    )}
    <span className="truncate flex-1">{att.name || 'Arquivo'}</span>
    {att.size ? (
      <span className="opacity-60 flex-shrink-0">{Math.round(att.size / 1024)}KB</span>
    ) : null}
    <ExternalLink size={10} className="flex-shrink-0 opacity-60" />
  </a>
));
AttachmentLink.displayName = 'AttachmentLink';

// Burbuja de mensaje memoizada (evita re-renders masivos)
const MessageRow = memo(({ msg, isMobile, compact }) => {
  const outbound = msg.direction === 'outbound' || msg.direction === 'sent';
  const msgBody = msg.message || msg.content?.body || '';
  const msgAttachments = msg.attachments || [];
  const timestamp = msg.timestamp || msg.createdAt || 0;
  const isFailed = msg.status === 'failed';

  const formatTime = useCallback((ts) => {
    if (!ts) return '';
    const date = new Date(ts);
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className={`flex mb-1.5 ${outbound ? 'justify-end' : 'justify-start'}`}
    >
      <div
        className={`
          max-w-[85%] px-3 py-2 rounded-2xl shadow-sm
          ${outbound
            ? 'bg-[#d9fdd3] text-gray-900 rounded-br-md'
            : 'bg-white text-gray-900 rounded-bl-md border border-gray-100'
          }
          ${isFailed ? 'border-red-300 opacity-80' : ''}
        `}
      >
        {/* Attachments primero (si hay) */}
        {msgAttachments.length > 0 && (
          <div className="mb-1.5 space-y-1">
            {msgAttachments.map((att, idx) => (
              <AttachmentLink key={idx} att={att} outbound={outbound} />
            ))}
          </div>
        )}

        {/* Texto del mensaje */}
        {msgBody && (
          <p className={`leading-relaxed whitespace-pre-wrap break-words ${
            isMobile || compact ? 'text-xs' : 'text-sm'
          }`}>
            {msgBody}
          </p>
        )}

        {/* Sin contenido */}
        {!msgBody && !msgAttachments.length && (
          <p className={`italic text-gray-400 ${isMobile || compact ? 'text-xs' : 'text-sm'}`}>
            Mensagem vazia
          </p>
        )}

        {/* Footer: hora + estado */}
        <div className={`flex items-center gap-1 mt-1 ${outbound ? 'justify-end' : 'justify-start'}`}>
          <span className={`select-none ${outbound ? 'text-gray-400' : 'text-gray-400'} ${
            isMobile || compact ? 'text-[9px]' : 'text-[10px]'
          }`}>
            {formatTime(timestamp)}
          </span>
          {outbound && <StatusIcon status={msg.status} />}
        </div>

        {/* Indicador de fallo */}
        {isFailed && (
          <div className="flex items-center gap-1 mt-1 text-red-600 text-[10px]">
            <AlertCircle size={10} />
            <span>Falha no envio</span>
          </div>
        )}
      </div>
    </motion.div>
  );
});
MessageRow.displayName = 'MessageRow';

// ============================================================================
// 🎯 COMPONENTE PRINCIPAL
// ============================================================================

const ClientMessageHistory = ({ clientId, compact = false }) => {
  // --------------------------------------------------------------------------
  // 📍 HOOKS
  // --------------------------------------------------------------------------
  const isMobile = useIsMobile();
  const containerRef = useRef(null);
  const prevScrollHeightRef = useRef(0);
  const isLoadingOlderFlag = useRef(false);
  const [showScrollButton, setShowScrollButton] = useState(false);

  // 🔥 Hook del feature module (sistema nuevo)
  const {
    conversationMessages,
    loading,
    error,
    hasMore,
    isLoadingOlder,
    loadOlderMessages,
    loadConversation,
    isEmpty,
  } = useMensagemDetalhe({
    autoLoad: false,
  });

  // --------------------------------------------------------------------------
  // 🚀 CARGA INICIAL
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (clientId) {
      loadConversation(clientId);
    }
  }, [clientId, loadConversation]);

  // --------------------------------------------------------------------------
  // 📏 MANTENER POSICIÓN DE SCROLL AL CARGAR ARRIBA
  // --------------------------------------------------------------------------
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !isLoadingOlderFlag.current) return;

    const prevHeight = prevScrollHeightRef.current;
    const newHeight = container.scrollHeight;

    if (prevHeight > 0 && newHeight > prevHeight) {
      // Ajustar scroll para mantener posición visual
      const heightDiff = newHeight - prevHeight;
      container.scrollTop += heightDiff;
      isLoadingOlderFlag.current = false;
    }
  }, [conversationMessages]);

  // --------------------------------------------------------------------------
  // 📜 SCROLL INFINITO HACIA ARRIBA
  // --------------------------------------------------------------------------
  const handleScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;

    // Mostrar/ocultar botón "bajar"
    setShowScrollButton(scrollHeight - scrollTop - clientHeight > 300);

    // Detectar scroll al top → cargar más
    if (scrollTop <= SCROLL_THRESHOLD && hasMore && !isLoadingOlder && !isLoadingOlderFlag.current) {
      prevScrollHeightRef.current = scrollHeight;
      isLoadingOlderFlag.current = true;
      loadOlderMessages();
    }
  }, [hasMore, isLoadingOlder, loadOlderMessages]);

  // --------------------------------------------------------------------------
  // 📤 SCROLL TO BOTTOM
  // --------------------------------------------------------------------------
  const scrollToBottom = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
  }, []);

  // --------------------------------------------------------------------------
  // 📅 AGRUPAR MENSAJES POR FECHA (Memoizado)
  // --------------------------------------------------------------------------
  const groupedMessages = useMemo(() => {
    if (!conversationMessages.length) return [];

    // Invertir: más antiguo arriba, más reciente abajo
    const ordered = [...conversationMessages].reverse();
    const groups = [];
    let currentLabel = null;
    let currentGroup = [];

    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    for (const msg of ordered) {
      const timestamp = msg.timestamp || msg.createdAt || 0;
      const date = new Date(timestamp);

      let label;
      if (date.toDateString() === today.toDateString()) {
        label = 'Hoje';
      } else if (date.toDateString() === yesterday.toDateString()) {
        label = 'Ontem';
      } else {
        label = date.toLocaleDateString('pt-BR', {
          day: '2-digit',
          month: 'long',
          year: 'numeric',
        });
      }

      if (label !== currentLabel) {
        if (currentGroup.length) {
          groups.push({ label: currentLabel, messages: currentGroup });
        }
        currentLabel = label;
        currentGroup = [msg];
      } else {
        currentGroup.push(msg);
      }
    }

    if (currentGroup.length) {
      groups.push({ label: currentLabel, messages: currentGroup });
    }

    return groups;
  }, [conversationMessages]);

  // --------------------------------------------------------------------------
  // 🎨 RENDER: LOADING
  // --------------------------------------------------------------------------
  if (loading) {
    return (
      <div className={`bg-white border border-slate-200 rounded-2xl shadow-sm ${
        isMobile || compact ? 'p-3' : 'p-4'
      }`}>
        {/* Header skeleton */}
        <div className="flex items-center justify-between mb-3">
          <div className="h-5 w-28 bg-slate-200 rounded animate-pulse" />
          <div className="h-5 w-10 bg-slate-200 rounded-full animate-pulse" />
        </div>
        {/* Mensajes skeleton */}
        <div className="space-y-2">
          <div className="h-14 w-4/5 bg-slate-100 rounded-2xl animate-pulse ml-auto" />
          <div className="h-10 w-3/4 bg-slate-100 rounded-2xl animate-pulse" />
          <div className="h-16 w-5/6 bg-slate-100 rounded-2xl animate-pulse ml-auto" />
          <div className="h-12 w-2/3 bg-slate-100 rounded-2xl animate-pulse" />
        </div>
      </div>
    );
  }

  // --------------------------------------------------------------------------
  // 🎨 RENDER: ERROR (solo si no hay datos)
  // --------------------------------------------------------------------------
  if (error && isEmpty) {
    return (
      <div className={`bg-white border border-slate-200 rounded-2xl shadow-sm ${
        isMobile || compact ? 'p-3' : 'p-4'
      }`}>
        <div className="flex items-center justify-between mb-3">
          <h2 className={`font-semibold text-slate-900 flex items-center gap-1.5 ${
            isMobile || compact ? 'text-sm' : 'text-base'
          }`}>
            <MessageSquare className={isMobile || compact ? 'w-4 h-4' : 'w-5 h-5'} style={{ color: '#10b981' }} />
            Mensagens
          </h2>
        </div>
        <div className="text-center py-6">
          <AlertCircle className="mx-auto text-red-400 mb-2 w-8 h-8" />
          <p className="text-sm text-red-500">Erro ao carregar mensagens</p>
          <button
            onClick={() => clientId && loadConversation(clientId)}
            className="mt-2 text-xs text-blue-600 hover:text-blue-700 underline"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  // --------------------------------------------------------------------------
  // 🎨 RENDER PRINCIPAL
  // --------------------------------------------------------------------------
  return (
    <div className={`bg-white border border-slate-200 rounded-2xl shadow-sm relative ${
      isMobile || compact ? 'p-3' : 'p-4'
    }`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h2 className={`font-semibold text-slate-900 flex items-center gap-1.5 ${
          isMobile || compact ? 'text-sm' : 'text-base'
        }`}>
          <MessageSquare
            className={isMobile || compact ? 'w-4 h-4' : 'w-5 h-5'}
            style={{ color: '#10b981' }}
          />
          {isMobile || compact ? 'Mensagens' : 'Histórico de Mensagens'}
        </h2>
        {!isEmpty && (
          <span className={`bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full font-medium ${
            isMobile || compact ? 'text-[9px]' : 'text-xs'
          }`}>
            {conversationMessages.length}
          </span>
        )}
      </div>

      {/* Contenido */}
      {isEmpty ? (
        <div className={`text-center ${isMobile || compact ? 'py-8' : 'py-10'}`}>
          <MessageSquare className={`mx-auto text-slate-200 mb-2 ${
            isMobile || compact ? 'w-8 h-8' : 'w-10 h-10'
          }`} />
          <p className={`text-slate-400 ${isMobile || compact ? 'text-xs' : 'text-sm'}`}>
            Nenhuma mensagem enviada
          </p>
          <p className="text-[10px] text-slate-300 mt-1">
            As mensagens aparecerão ao enviar WhatsApp
          </p>
        </div>
      ) : (
        <>
          {/* Área de mensajes con scroll */}
          <div
            ref={containerRef}
            onScroll={handleScroll}
            className="overflow-y-auto overscroll-contain scroll-smooth"
            style={{
              maxHeight: isMobile || compact ? MAX_HEIGHT_MOBILE : MAX_HEIGHT_DESKTOP,
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'thin',
              scrollbarColor: '#e2e8f0 transparent',
            }}
          >
            {/* Indicador de carga arriba */}
            <AnimatePresence>
              {isLoadingOlder && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="flex items-center justify-center py-2"
                >
                  <Loader2 size={14} className="animate-spin text-slate-400" />
                  <span className="ml-2 text-[10px] text-slate-400">
                    Carregando mensagens anteriores...
                  </span>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Indicador "hay más" */}
            {hasMore && !isLoadingOlder && (
              <div className="flex items-center justify-center py-1.5">
                <span className="text-[9px] text-slate-300 bg-slate-50 rounded-full px-2 py-0.5">
                  Role para ver mais
                </span>
              </div>
            )}

            {/* Grupos de mensajes */}
            <div className="space-y-0.5">
              {groupedMessages.map((group) => (
                <div key={group.label}>
                  <DateLabel label={group.label} />
                  {group.messages.map((msg, idx) => (
                    <MessageRow
                      key={msg.id || idx}
                      msg={msg}
                      isMobile={isMobile}
                      compact={compact}
                    />
                  ))}
                </div>
              ))}
            </div>

            {/* Referencia para scroll bottom */}
            <div className="h-1" />
          </div>

          {/* Botón bajar (flotante) */}
          <AnimatePresence>
            {showScrollButton && (
              <motion.button
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                onClick={scrollToBottom}
                className="absolute bottom-3 right-3 z-10 p-1.5 bg-white rounded-full shadow-lg
                           border border-slate-200 text-slate-500 hover:text-slate-700
                           hover:shadow-xl transition-all"
                aria-label="Ver últimas mensagens"
              >
                <ChevronDown size={16} />
              </motion.button>
            )}
          </AnimatePresence>
        </>
      )}

      {/* Footer informativo */}
      <div className={`text-slate-300 border-t border-slate-50 pt-2 mt-2 ${
        isMobile || compact ? 'text-[8px]' : 'text-[10px]'
      }`}>
        Mensagens registradas automaticamente ao usar o WhatsApp
      </div>
    </div>
  );
};

// ============================================================================
// 📤 EXPORTACIÓN
// ============================================================================
ClientMessageHistory.displayName = 'ClientMessageHistory';
export { ClientMessageHistory };
export default ClientMessageHistory;